import React from 'react';
import { Link } from 'react-router-dom';
import SharedLayout from '@/components/SharedLayout';
import SubpageNavigation from '@/components/SubpageNavigation';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation';
import SEOHead from '@/components/SEOHead';
import { CANONICAL_URLS } from '@/utils/canonicalUrls';

const Eisenhower = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Eisenhower Matrix: Free Priority Management Tool & Guide",
    "description": "Learn how to use the Eisenhower Matrix (Urgent-Important Matrix) for effective task prioritization. Free online tool with complete guide.",
    "author": { "@type": "Organization", "name": "Free Pomodoro Timer" },
    "publisher": { "@type": "Organization", "name": "Free Pomodoro Timer", "url": "https://freepomodorotimer.com", "logo": { "@type": "ImageObject", "url": "https://freepomodorotimer.com/favicon.ico" } },
    "datePublished": "2024-01-15",
    "dateModified": "2026-03-13",
    "image": "https://freepomodorotimer.com/og-image.jpg",
    "mainEntityOfPage": CANONICAL_URLS.eisenhower
  };

  return (
    <>
      <SEOHead
        title="Eisenhower Matrix | Free Task Priority Tool & Guide"
        description="Master task prioritization with the free Eisenhower Matrix tool. Learn the 4-quadrant urgent-important framework for better time management, reduced stress & higher productivity."
        keywords="eisenhower matrix, eisenhower matrix template, urgent important matrix, task prioritization tool, priority matrix, time management framework, eisenhower box, decision matrix, free productivity tool"
        canonicalUrl={CANONICAL_URLS.eisenhower}
        schemaData={schemaData}
      />
      <SharedLayout>
        <article className="text-left px-6 py-12 max-w-4xl mx-auto">
          <BreadcrumbNavigation />
          <SubpageNavigation />
        
        <h1 className="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100">Eisenhower Matrix: Free Task Priority Management Tool</h1>
        <p className="mb-4 text-gray-700 dark:text-gray-200">
          The <strong>Eisenhower Matrix</strong>, also known as the Urgent-Important Matrix or Eisenhower Box, is a decision-making framework developed by President Dwight D. Eisenhower. 
          This powerful <strong>task prioritization tool</strong> helps you categorize activities by urgency and importance, enabling better time management and reduced stress.
        </p>
        <p className="mb-6 text-gray-700 dark:text-gray-200">
          Used by CEOs, entrepreneurs, and productivity experts worldwide, this matrix transforms overwhelming to-do lists into clear, actionable priorities. 
          Learn how to escape the "urgency trap" and focus on what truly matters for long-term success.
        </p>

        <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-400 p-4 mb-8">
          <p className="text-amber-800 dark:text-amber-200">
            <strong>Quick Start:</strong> Combine the Eisenhower Matrix with our <Link to="/pomodoro" className="underline hover:text-amber-600">free Pomodoro Timer</Link> for 
            focused execution of your prioritized tasks, or organize your priorities visually with <Link to="/kanban" className="underline hover:text-amber-600">Kanban boards</Link>.
          </p>
        </div>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">The Four Quadrants of the Eisenhower Matrix</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="p-4 bg-red-100 dark:bg-red-900 rounded-lg">
            <h3 className="font-bold text-red-800 dark:text-red-200 mb-2">Quadrant 1: Do First</h3>
            <p className="text-red-700 dark:text-red-300 text-sm mb-2"><strong>Urgent & Important</strong></p>
            <p className="text-red-600 dark:text-red-400 text-sm">Crises, emergencies, deadline-driven projects. Handle these immediately.</p>
          </div>
          <div className="p-4 bg-blue-100 dark:bg-blue-900 rounded-lg">
            <h3 className="font-bold text-blue-800 dark:text-blue-200 mb-2">Quadrant 2: Schedule</h3>
            <p className="text-blue-700 dark:text-blue-300 text-sm mb-2"><strong>Not Urgent & Important</strong></p>
            <p className="text-blue-600 dark:text-blue-400 text-sm">Prevention, planning, development, recreation. The key to long-term success.</p>
          </div>
          <div className="p-4 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
            <h3 className="font-bold text-yellow-800 dark:text-yellow-200 mb-2">Quadrant 3: Delegate</h3>
            <p className="text-yellow-700 dark:text-yellow-300 text-sm mb-2"><strong>Urgent & Not Important</strong></p>
            <p className="text-yellow-600 dark:text-yellow-400 text-sm">Interruptions, some calls, some emails. Delegate or batch these.</p>
          </div>
          <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
            <h3 className="font-bold text-gray-800 dark:text-gray-200 mb-2">Quadrant 4: Eliminate</h3>
            <p className="text-gray-700 dark:text-gray-300 text-sm mb-2"><strong>Not Urgent & Not Important</strong></p>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Time wasters, busy work, excessive social media. Minimize or remove.</p>
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">How to Use the Eisenhower Matrix (Step-by-Step)</h2>
        <ol className="list-decimal pl-6 mb-6 text-gray-700 dark:text-gray-200">
          <li className="mb-3"><strong>Brain dump all tasks:</strong> Write down everything on your plate — work projects, personal goals, daily tasks</li>
          <li className="mb-3"><strong>Evaluate urgency:</strong> Ask "Does this need immediate attention?" Consider deadlines and consequences of delay</li>
          <li className="mb-3"><strong>Evaluate importance:</strong> Ask "Does this contribute to my long-term goals and values?"</li>
          <li className="mb-3"><strong>Categorize into quadrants:</strong> Place each task based on its urgency and importance levels</li>
          <li className="mb-3"><strong>Take action by quadrant:</strong> Do Q1 now, schedule Q2 time blocks, delegate Q3, eliminate Q4</li>
          <li className="mb-3"><strong>Review regularly:</strong> Reassess priorities weekly as circumstances change</li>
        </ol>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Strategic Time Allocation</h2>
        <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-lg mb-8">
          <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-100">Ideal Time Distribution</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">20-25%</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Q1: Do First</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">65-70%</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Q2: Schedule</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">15%</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Q3: Delegate</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">≤5%</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Q4: Eliminate</div>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Common Eisenhower Matrix Mistakes</h2>
        <ul className="list-disc pl-6 mb-8 text-gray-700 dark:text-gray-200">
          <li className="mb-3"><strong>Living in Quadrant 1:</strong> Constantly firefighting creates stress and prevents strategic thinking</li>
          <li className="mb-3"><strong>Confusing urgent with important:</strong> Email notifications feel urgent but may not advance your goals</li>
          <li className="mb-3"><strong>Neglecting Quadrant 2:</strong> Prevention, planning, and skill development prevent future crises</li>
          <li className="mb-3"><strong>Over-delegating:</strong> Some important tasks require your personal attention and expertise</li>
          <li className="mb-3"><strong>Analysis paralysis:</strong> Spend 2-3 minutes max categorizing each task, then move to action</li>
        </ul>

        <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Eisenhower Matrix Examples by Profession</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">For Students</h3>
            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
              <div><strong>Q1:</strong> Submit assignment due tomorrow</div>
              <div><strong>Q2:</strong> Create study schedule, learn new concepts</div>
              <div><strong>Q3:</strong> Group meeting for non-critical project</div>
              <div><strong>Q4:</strong> Social media, excessive gaming</div>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3 text-gray-800 dark:text-gray-100">For Professionals</h3>
            <div className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
              <div><strong>Q1:</strong> Client crisis, urgent bug fix</div>
              <div><strong>Q2:</strong> Strategic planning, skill development</div>
              <div><strong>Q3:</strong> Non-essential meetings, some emails</div>
              <div><strong>Q4:</strong> Office gossip, excessive social media</div>
            </div>
          </div>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-bold mb-3 text-green-800 dark:text-green-200">🎯 Combine Methods for Maximum Productivity</h3>
          <ul className="space-y-2 text-green-700 dark:text-green-300">
            <li>• Use <Link to="/pomodoro" className="font-semibold underline">Pomodoro sessions</Link> for focused work on Q1 and Q2 tasks</li>
            <li>• Organize prioritized tasks in <Link to="/kanban" className="font-semibold underline">Kanban boards</Link> for visual workflow</li>
            <li>• Schedule Q2 activities during your peak energy hours</li>
            <li>• Batch similar Q3 tasks to minimize context switching</li>
          </ul>
        </div>

        {/* Related Tools Section */}
        <section className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Free Productivity Tools</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Free Pomodoro Timer</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                After prioritizing with the Eisenhower Matrix, use focused 25-minute work sessions to tackle your Quadrant 1 and 2 tasks effectively.
              </p>
              <Link to="/pomodoro" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                Start Pomodoro Timer →
              </Link>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Free Kanban Board</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Track the progress of your prioritized tasks visually. Move important tasks through your workflow from "To Do" to "Done".
              </p>
              <Link to="/kanban" className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">
                Try Kanban Board →
              </Link>
            </div>
          </div>
        </section>
        </article>
      </SharedLayout>
    </>
  );
};

export default Eisenhower;